<template>
  <div
    ref="PsTooltip"
    class="psui-el-tooltip"
    @mouseenter="open"
    @mouseleave="close"
    @focusin="open"
    @focusout="close"
  >
    <div
      ref="PsTooltipTrigger"
      class="psui-el-tooltip-trigger"
    >
      <slot name="trigger" />
    </div>

    <div class="psui-el-tooltip-wrapper">
      <div
        role="tooltip"
        ref="PsTooltipDialog"
        class="psui-el-tooltip-dialog"
        :class="cssClass"
      >
        <div
          class="psui-el-tooltip-content"
          :class="getComponentClass"
        >
          <h2 v-if="title">
            {{ title }}
          </h2>
          <div
            v-else
            class="psui-el-tooltip-content-wrapper"
          >
            <slot name="content" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  /**
   * It sets the title of the tooltip if needed.
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * It disables the dialog section.
   */
  ignoreDialog: {
    type: Boolean,
    default: false,
  },
  /**
   * It sets the layout of the tooltip if needed. Usefull when using rich or dialog tooltip.
   */
  layout: {
    type: String,
    default: '',
  },
  /**
   * It sets a additional styling if needed.
   */
  cssClass: {
    type: String,
    default: '',
  },
  /**
   * It sets the vertical position.
   */
  position: {
    type: String,
    default: 'bottom',
    validator: (value) => ['bottom', 'top', 'custom'].includes(value),
  },
  /**
   * It sets the custom positions.
   */
  customPosition: {
    type: String,
    default: '',
  },
  /**
   * It delays showing the tooltip on hover, in milliseconds.
   */
  delay: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['show', 'close'])

const show = ref(false)
const PsTooltip = ref(null)
const PsTooltipTrigger = ref(null)
const PsTooltipDialog = ref(null)
let openTimer = null

const getComponentClass = computed(() => {
  return `layout-${props.layout}`
})

onMounted(() => {
  document.addEventListener('resize', updatePosition)
  
})

onBeforeUnmount(() => {
  if (openTimer) clearTimeout(openTimer)
  document.removeEventListener('resize', updatePosition)
  document.removeEventListener('keydown', handleKeyDown)
})

const open = () => {
  if (show.value || props.ignoreDialog || openTimer) return
  if (props.delay > 0) {
    openTimer = setTimeout(() => {
      openTimer = null
      showTooltip()
    }, props.delay)
  } else {
    showTooltip()
  }
}

const showTooltip = () => {
  emit('show')
  show.value = true

  document.addEventListener('keydown', handleKeyDown)

  const dialog = PsTooltipDialog.value
  dialog.style.display = 'block'
  dialog.style.opacity = 0
  setTimeout(() => {
    updatePosition()
  }, 10)
}

const close = () => {
  if (openTimer) {
    clearTimeout(openTimer)
    openTimer = null
  }
  const dialog = PsTooltipDialog.value
  if (show.value && dialog) {
    emit('close')
    show.value = false
    dialog.style.display = 'none'

    document.removeEventListener('keydown', handleKeyDown)
  }
}

const handleKeyDown = (event) => {
  if (event.key === 'Escape' && show.value) {
    close()
  }
}


const updatePosition = () => {
  const dialog = PsTooltipDialog.value
  const trigger = PsTooltipTrigger.value
  const rectDialog = dialog?.getBoundingClientRect()
  const rectTrigger = trigger?.getBoundingClientRect()
  const windowWidth = document.documentElement.clientWidth
  if(dialog) dialog.style.top = `${rectTrigger.y + rectTrigger.height + 8}px`
  if (rectTrigger && rectTrigger.x + rectDialog.width + 20 > windowWidth) {
    dialog.style.left = `${windowWidth - rectDialog.width - 30}px`
  } else if (rectTrigger && rectTrigger.x - rectDialog.width < 0) {
    dialog.style.left = `${rectTrigger.x + 10}px`
  } else if (rectTrigger && rectDialog.width >= rectTrigger.width && props.position != 'custom') {
    dialog.style.left = `${rectTrigger.x - (rectDialog.width - rectTrigger.width) / 2}px`
  } else if(dialog) {
    dialog.style.left = `${rectTrigger.x + (rectTrigger.width - rectDialog.width) / 2}px`
  }
  if (props.position == 'top') {
    dialog.style.top = `${rectTrigger.y - rectDialog.height - 10}px`
  }
  if (props.position == 'custom') {
    const hasFixedPosition = props.customPosition.includes('position: fixed')
    dialog.style = props.customPosition
    dialog.style.display = 'block'
    dialog.style.position = hasFixedPosition ? 'fixed' : 'absolute'
  }
  setTimeout(() => {
    if(dialog) dialog.style.opacity = 100
  }, 100)
}
</script>
